import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import ExpandableCard from 'src/components/ExpandableCard';
import FeaturesContext from 'src/context/FeaturesContext';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  PIECE_STATUS_BADGE_BORDER_COLORS,
  PIECE_STATUS_BADGE_COLORS,
  PIECE_STATUS_BADGE_TEXT_COLORS,
} from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import { getUuid } from 'src/utils/url';

import MaterialChips from './_matherial';

const PieceHeader = ({ piece, runUUID }) => {
  const [material, setMaterial] = useState(null);
  const [materialBatch, setMaterialBatch] = useState(null);
  const [isMaterialLoading, setIsMaterialLoading] = useState(false);
  const { features } = useContext(FeaturesContext);
  const isMaterialManagementFeatureEnabled = isFeatureEnabled(features, FEATURES.MATERIAL_MANAGEMENT);

  const { addToast } = useToasts();

  const getMaterialInfo = async () => {
    if (!runUUID) return;

    try {
      setIsMaterialLoading(true);
      const run = await api.get(`${API_RESOURCES.RUN}/${runUUID}`).json();

      const printerUUID = getUuid(run.printer);

      if (!printerUUID) {
        setIsMaterialLoading(false);

        const prints = await api.get(
          `${API_RESOURCES.PRINT}/`,
          {
            searchParams: {
              'filter[piece]': piece.uri,
            },
          }
        ).json();

        const runURIs = prints.resources.map((print) => print.run).filter(Boolean);

        const runActuals = await api.get(
          `${API_RESOURCES.RUN_ACTUALS}/`,
          {
            searchParams: {
              'filter[run]': runURIs,
            },
          }
        ).json();

        const materialBatchUUID = getUuid(runActuals.resources[0]?.material_batch);

        if (materialBatchUUID) {
          const materialBatch = await api.get(
            `${API_RESOURCES.MATERIAL_BATCH}/${materialBatchUUID}/`
          ).json();

          setMaterialBatch(materialBatch);

          const materialUUID = getUuid(materialBatch?.materials[0].uri);

          if (materialUUID) {
            const material = await await api.get(`${API_RESOURCES.MATERIAL}/${materialUUID}/`).json();
            setMaterial(material);
          }

          setIsMaterialLoading(false);

          // eslint-disable-next-line camelcase
          const materialLotUUID = getUuid(materialBatch?.material_lots[0]);

          if (materialLotUUID) {
            await api.get(`${API_RESOURCES.MATERIAL_LOT}/${materialLotUUID}/`).json();
          }
        }

        return;
      }

      if (isMaterialManagementFeatureEnabled) {

        const printer = await api.get(`${API_RESOURCES.PRINTER}/${printerUUID}/`).json();

        const { resources: materialBatchesAtMachine } = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/`, {
          searchParams: {
            'filter[at_machine]': printer.uri,
          },
        }).json();

        const materialBatch = materialBatchesAtMachine[0];

        setMaterialBatch(materialBatch);

        const materialUUID = getUuid(materialBatch?.materials[0].uri);

        if (materialUUID) {
          const material = await await api.get(`${API_RESOURCES.MATERIAL}/${materialUUID}/`).json();
          setMaterial(material);
        }

        setIsMaterialLoading(false);

        // eslint-disable-next-line camelcase
        const materialLotUUID = getUuid(materialBatch?.material_lots[0]);

        if (materialLotUUID) {
          await api.get(`${API_RESOURCES.MATERIAL_LOT}/${materialLotUUID}/`).json();
        }

      }

    } catch (error) {
      addToast('There was an error. Please try again.', { appearance: 'error' });
      console.error(error);
    } finally {
      setIsMaterialLoading(false);
    }
  };

  useEffect(() => {
    getMaterialInfo();
  }, []);

  const pieceStatusBadgeStyles = {
    backgroundColor: PIECE_STATUS_BADGE_COLORS[piece.status],
    border: `1px solid ${PIECE_STATUS_BADGE_BORDER_COLORS[piece.status]}`,
    color: PIECE_STATUS_BADGE_TEXT_COLORS[piece.status],
    textTransform: 'capitalize',
    fontSize: '0.9rem',
    fontWeight: 500,
  };

  return (
    <ExpandableCard
      defaultExpanded
      className="piece-status run-workstation-body card-base text-center"
      isCollapsible={false}
      renderHeader={() => (
        <div className="d-flex align-items-center justify-content-center">
          <h4 className="mb-0 mr-2">
            Status:
          </h4>
          <span
            className="run-header-card__badge"
            style={pieceStatusBadgeStyles}
          >
            {piece.status}
          </span>
        </div>
      )}
    >
      <MaterialChips
        material={material}
        materialBatch={materialBatch}
        isMaterialLoading={isMaterialLoading}
      />
    </ExpandableCard>
  );
};

PieceHeader.propTypes = {
  runUUID: PropTypes.string.isRequired,
  piece: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    material: PropTypes.string.isRequired,
  }).isRequired,
};

export default PieceHeader;
